import React, { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, X, Send, Loader2, MessageSquare } from "lucide-react";

/**
 * Contextual AI Assistant widget.
 *
 * Add to any screen with an optional `context` prop:
 *   <AIAssistantWidget context={{ filing_id, screen: "filing" }} />
 *
 * If no context is passed, we infer sensible defaults from the URL.
 */
export default function AIAssistantWidget({ context: contextProp }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("crs_assistant_session") || null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef();
  const location = useLocation();
  const params = useParams();

  const context = contextProp || inferContext(location.pathname, params);

  useEffect(() => {
    if (open && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    if (!open || !sessionId) return;
    api.get(`/assistant/history/${sessionId}`).then(({ data }) => setMessages(data)).catch(() => {});
  }, [open, sessionId]);

  const suggestions = getSuggestions(context.screen);

  const send = async (q) => {
    const question = (q || text).trim();
    if (!question) return;
    setBusy(true);
    setMessages(prev => [...prev, { role: "user", content: question, at: new Date().toISOString() }]);
    setText("");
    try {
      const { data } = await api.post("/assistant/ask", { question, session_id: sessionId, context });
      if (!sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem("crs_assistant_session", data.session_id);
      }
      setMessages(prev => [...prev, {
        role: "assistant", content: data.reply, at: new Date().toISOString(),
        provider: data.provider, model: data.model,
      }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: "assistant", content: "Something went wrong. Try again in a moment.", at: new Date().toISOString() }]);
    } finally { setBusy(false); }
  };

  const reset = () => {
    localStorage.removeItem("crs_assistant_session");
    setSessionId(null); setMessages([]);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Open AI Tax Assistant"
        className="fixed bottom-24 right-5 z-50 grid h-14 w-14 place-items-center rounded-full bg-primary text-primary-foreground shadow-xl ring-4 ring-primary/20 hover:scale-105 transition-transform"
        data-testid="assistant-toggle"
      >
        <Sparkles className="h-6 w-6" />
      </button>

      {open && (
        <div className="fixed bottom-44 right-5 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[85vh] rounded-xl border border-border bg-background shadow-2xl flex flex-col overflow-hidden" data-testid="assistant-panel">
          <div className="flex items-center justify-between border-b border-border px-4 py-3 bg-gradient-to-r from-primary/10 to-transparent">
            <div className="flex items-center gap-2">
              <div className="grid h-8 w-8 place-items-center rounded-full bg-primary text-primary-foreground">
                <Sparkles className="h-4 w-4" />
              </div>
              <div className="leading-tight">
                <div className="font-display text-sm font-bold">AI Tax Assistant</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{context.screen || "assistant"}</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={reset} className="text-[10px] text-muted-foreground hover:text-foreground uppercase tracking-wide">Reset</button>
              <button onClick={() => setOpen(false)} className="grid h-8 w-8 place-items-center rounded-md hover:bg-muted"><X className="h-4 w-4" /></button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-center pt-2">
                <MessageSquare className="h-8 w-8 mx-auto text-muted-foreground/60" />
                <div className="mt-3 text-sm font-medium">Ask anything about {context.screen === "filing" ? "this filing" : context.screen === "ais_review" ? "this reconciliation" : context.screen === "client_detail" ? "this client" : "Indian tax"}.</div>
                <div className="mt-1 text-xs text-muted-foreground">Grounded in your data. Trained on Indian ITR rules.</div>
                <div className="mt-4 space-y-1.5">
                  {suggestions.map((s, i) => (
                    <button key={i} onClick={() => send(s)} className="w-full text-left rounded border border-border bg-muted/30 px-3 py-2 text-xs hover:bg-muted" data-testid={`suggestion-${i}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                  {m.content}
                  {m.model && <div className="mt-1 text-[10px] opacity-60">{m.provider}/{m.model}</div>}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="rounded-lg bg-muted px-3 py-2 text-sm flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> thinking…
                </div>
              </div>
            )}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-border p-2 flex gap-2">
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask…"
              className="min-h-[40px] max-h-32 resize-none text-sm"
              data-testid="assistant-input"
            />
            <Button type="submit" size="icon" disabled={busy || !text.trim()} data-testid="assistant-send"><Send className="h-4 w-4" /></Button>
          </form>
        </div>
      )}
    </>
  );
}

function inferContext(pathname, params) {
  if (pathname.startsWith("/practice/clients/") && params.clientId) return { client_id: params.clientId, screen: "client_detail" };
  if (pathname.startsWith("/ca-portal/filings/") && params.filingId) return { filing_id: params.filingId, screen: "ca_filing" };
  if (pathname.startsWith("/filing")) return { screen: "filing" };
  if (pathname.startsWith("/ais")) return { screen: "ais_review" };
  if (pathname.startsWith("/practice")) return { screen: "practice" };
  if (pathname.startsWith("/ca-portal")) return { screen: "ca_portal" };
  if (pathname.startsWith("/dashboard")) return { screen: "dashboard" };
  return { screen: "assistant" };
}

function getSuggestions(screen) {
  switch (screen) {
    case "filing":
      return [
        "Should I choose the old or new regime this year?",
        "What deductions am I missing?",
        "Explain any validation errors in this return.",
      ];
    case "ais_review":
      return [
        "Summarize the mismatches between AIS and my return.",
        "Which fields should I approve first?",
        "How do I explain a mismatch if I get a 143(1) notice?",
      ];
    case "client_detail":
      return [
        "Summarize this client's filing history.",
        "Draft an email requesting missing documents.",
        "Which schedules typically apply to this client?",
      ];
    case "ca_filing":
      return [
        "Review this return for red flags before I file.",
        "Compare old vs new regime for this client.",
        "Which sections are underused here?",
      ];
    default:
      return [
        "Explain the standard deduction for AY 2025-26.",
        "Old vs new regime — which one is default?",
        "Due dates for individual ITR this year?",
      ];
  }
}
